/**
 * Tampon de lignes de log du panneau technique (§3.3).
 *
 * Le flux d'un build Docker peut produire plusieurs milliers de lignes : on
 * n'en conserve que les dernières en mémoire, sans quoi le navigateur finit par
 * ramer sur la page de progression elle-même.
 */

import type { SseLogLine } from '@portail/shared';

/** Nombre maximal de lignes conservées côté client (§3.3). */
export const MAX_LOG_LINES = 2000;

/**
 * Ligne de log stockée. Le numéro de séquence n'existe que pour les lignes
 * reçues en direct : celles du `snapshot` sont relues en base et n'en portent
 * pas.
 */
export interface LogEntry extends SseLogLine {
  seq: number | null;
}

/** Ajoute des lignes en fin de tampon et rogne les plus anciennes. */
export function appendLogs(
  existing: readonly LogEntry[],
  incoming: readonly LogEntry[],
  max: number = MAX_LOG_LINES,
): LogEntry[] {
  if (incoming.length === 0) return existing as LogEntry[];
  const merged = [...existing, ...incoming];
  return merged.length > max ? merged.slice(merged.length - max) : merged;
}

/**
 * Reconstruit le tampon à la réception d'un `snapshot`.
 *
 * Le snapshot rejoue les dernières lignes persistées ; les lignes reçues en
 * direct dont le numéro de séquence dépasse celui du snapshot lui sont
 * postérieures et doivent être conservées, sans quoi une reconnexion perdrait
 * les toutes dernières lignes du flux.
 */
export function mergeSnapshotLogs(
  snapshotLogs: readonly SseLogLine[],
  live: readonly LogEntry[],
  snapshotSeq: number,
  max: number = MAX_LOG_LINES,
): LogEntry[] {
  const replayed: LogEntry[] = snapshotLogs.map((line) => ({ ...line, seq: null }));
  const newer = live.filter((entry) => entry.seq !== null && entry.seq > snapshotSeq);
  return appendLogs(replayed, newer, max);
}

/** Extrait les `count` dernières lignes, formatées pour un rapport texte. */
export function tailLines(logs: readonly LogEntry[], count: number): LogEntry[] {
  return logs.length > count ? logs.slice(logs.length - count) : [...logs];
}
